import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import PortalLayout from "@/components/ventrix/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CreditCard, Save, Landmark, Smartphone, Banknote } from "lucide-react";

function MethodHead({ icon: Icon, title, enabled, onToggle, testid }) {
  return (
    <div className="flex items-center justify-between">
      <div className="vtx-eyebrow inline-flex items-center gap-1.5"><Icon size={13} /> {title}</div>
      <label className="inline-flex items-center gap-2 text-xs font-semibold text-[#0E2F76]">
        <input type="checkbox" checked={enabled} onChange={onToggle} data-testid={testid} /> Enabled
      </label>
    </div>
  );
}

const blank = () => ({
  manual_enabled: true, manual_label: "Bank transfer",
  mpesa_manual_enabled: true, mpesa_manual_label: "M-Pesa (Paybill)",
  cash_enabled: true, cash_label: "Cash",
  stripe_enabled: false, stripe_label: "Card",
  stripe_publishable_key: "", stripe_secret_key: "", stripe_webhook_secret: "",
  mpesa_enabled: false, mpesa_label: "M-Pesa (STK push)",
  mpesa_consumer_key: "", mpesa_consumer_secret: "", mpesa_passkey: "",
  mpesa_shortcode: "", mpesa_env: "sandbox", mpesa_callback_url: "", mpesa_callback_token: "",
});

export default function PaymentsSettings() {
  const [f, setF] = useState(blank());
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (k) => (e) => setF({ ...f, [k]: e.target.checked });

  useEffect(() => {
    api.get("/v1/admin/settings/payments")
      .then(({ data }) => {
        const m = (data.data || {}).methods || {};
        setF({
          manual_enabled: m.manual?.enabled !== undefined ? m.manual.enabled : true,
          manual_label: m.manual?.label || "Bank transfer",
          mpesa_manual_enabled: m.mpesa_manual?.enabled !== undefined ? m.mpesa_manual.enabled : true,
          mpesa_manual_label: m.mpesa_manual?.label || "M-Pesa (Paybill)",
          cash_enabled: m.cash?.enabled !== undefined ? m.cash.enabled : true,
          cash_label: m.cash?.label || "Cash",
          stripe_enabled: !!m.stripe?.enabled,
          stripe_label: m.stripe?.label || "Card",
          stripe_publishable_key: m.stripe?.config?.publishable_key || "",
          stripe_secret_key: m.stripe?.config?.secret_key || "",   // "••••••" if set
          stripe_webhook_secret: m.stripe?.config?.webhook_secret || "",
          mpesa_enabled: !!m.mpesa?.enabled,
          mpesa_label: m.mpesa?.label || "M-Pesa (STK push)",
          mpesa_consumer_key: m.mpesa?.config?.consumer_key || "",
          mpesa_consumer_secret: m.mpesa?.config?.consumer_secret || "",
          mpesa_passkey: m.mpesa?.config?.passkey || "",
          mpesa_shortcode: m.mpesa?.config?.shortcode || "",
          mpesa_env: m.mpesa?.config?.env || "sandbox",
          mpesa_callback_url: m.mpesa?.config?.callback_url || "",
          mpesa_callback_token: m.mpesa?.config?.callback_token || "",
        });
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load payment settings"))
      .finally(() => setLoaded(true));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { data: { methods: {
        manual: { enabled: f.manual_enabled, label: f.manual_label },
        mpesa_manual: { enabled: f.mpesa_manual_enabled, label: f.mpesa_manual_label },
        cash: { enabled: f.cash_enabled, label: f.cash_label },
        stripe: { enabled: f.stripe_enabled, label: f.stripe_label,
                  config: { publishable_key: f.stripe_publishable_key, secret_key: f.stripe_secret_key,
                            webhook_secret: f.stripe_webhook_secret } },
        mpesa: { enabled: f.mpesa_enabled, label: f.mpesa_label,
                 config: { consumer_key: f.mpesa_consumer_key, consumer_secret: f.mpesa_consumer_secret,
                           passkey: f.mpesa_passkey, shortcode: f.mpesa_shortcode,
                           env: f.mpesa_env, callback_url: f.mpesa_callback_url,
                           callback_token: f.mpesa_callback_token } },
      }}};
      await api.put("/v1/admin/settings/payments", payload);
      toast.success("Payment settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <PortalLayout section="admin">
      <div className="vtx-eyebrow">/ Admin · Settings</div>
      <h1 className="mt-2 text-3xl font-black text-[#0E2F76] tracking-tighter inline-flex items-center gap-2"><CreditCard size={24} /> Payments</h1>
      <p className="text-sm text-[#475569] mt-1">Turn payment methods on or off and manage gateway keys. Secret keys are stored encrypted and never shown again — leave a key blank to keep the saved one. Only enabled methods appear to customers.</p>

      {!loaded ? (
        <div className="vtx-card mt-6 p-6"><div className="vtx-skeleton h-40" /></div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          {/* Manual */}
          <div className="vtx-card p-6 space-y-4">
            <MethodHead icon={Landmark} title="Bank transfer / manual" enabled={f.manual_enabled} onToggle={toggle("manual_enabled")} testid="manual-enabled" />
            <label className="block"><span className="vtx-eyebrow block mb-1.5">Label shown to customers</span><input className="vtx-input" value={f.manual_label} onChange={set("manual_label")} data-testid="manual-label" /></label>
            <p className="text-[11px] text-[#475569]">Off-platform payments (bank transfer, cheque). Recorded against the invoice with a reference — no gateway keys needed.</p>
          </div>

          {/* M-Pesa (manual Paybill) */}
          <div className="vtx-card p-6 space-y-4">
            <MethodHead icon={Smartphone} title="M-Pesa (Paybill / manual)" enabled={f.mpesa_manual_enabled} onToggle={toggle("mpesa_manual_enabled")} testid="mpesa-manual-enabled" />
            <label className="block"><span className="vtx-eyebrow block mb-1.5">Label shown to customers</span><input className="vtx-input" value={f.mpesa_manual_label} onChange={set("mpesa_manual_label")} data-testid="mpesa-manual-label" /></label>
            <p className="text-[11px] text-[#475569]">The customer pays to your M-Pesa Paybill and enters the confirmation code — recorded against the invoice and held pending until your team verifies it. No Daraja keys needed. Set the Paybill number under Settings → Company.</p>
          </div>

          {/* Cash */}
          <div className="vtx-card p-6 space-y-4">
            <MethodHead icon={Banknote} title="Cash" enabled={f.cash_enabled} onToggle={toggle("cash_enabled")} testid="cash-enabled" />
            <label className="block"><span className="vtx-eyebrow block mb-1.5">Label shown to customers</span><input className="vtx-input" value={f.cash_label} onChange={set("cash_label")} data-testid="cash-label" /></label>
            <p className="text-[11px] text-[#475569]">Cash paid in person. Recorded against the invoice (reference optional) and held pending until your team confirms it.</p>
          </div>

          {/* Stripe */}
          <div className="vtx-card p-6 space-y-4">
            <MethodHead icon={CreditCard} title="Stripe (card)" enabled={f.stripe_enabled} onToggle={toggle("stripe_enabled")} testid="stripe-enabled" />
            <label className="block"><span className="vtx-eyebrow block mb-1.5">Label</span><input className="vtx-input" value={f.stripe_label} onChange={set("stripe_label")} data-testid="stripe-label" /></label>
            <label className="block"><span className="vtx-eyebrow block mb-1.5">Publishable key (public)</span><input className="vtx-input font-mono" value={f.stripe_publishable_key} onChange={set("stripe_publishable_key")} placeholder="pk_live_… / pk_test_…" data-testid="stripe-pk" /></label>
            <label className="block"><span className="vtx-eyebrow block mb-1.5">Secret key (encrypted)</span><input className="vtx-input font-mono" value={f.stripe_secret_key} onChange={set("stripe_secret_key")} placeholder="leave blank to keep" data-testid="stripe-sk" /></label>
            <label className="block"><span className="vtx-eyebrow block mb-1.5">Webhook signing secret (encrypted)</span><input className="vtx-input font-mono" value={f.stripe_webhook_secret} onChange={set("stripe_webhook_secret")} placeholder="whsec_… · leave blank to keep" data-testid="stripe-whsec" /></label>
            <p className="text-[11px] text-[#475569]">Point a Stripe webhook at <span className="font-mono">/api/v1/payments/stripe/webhook</span> for the <span className="font-mono">payment_intent.succeeded</span> event, then paste its signing secret here.</p>
          </div>

          {/* M-Pesa */}
          <div className="vtx-card p-6 space-y-4 lg:col-span-2">
            <MethodHead icon={Smartphone} title="M-Pesa STK push (Safaricom Daraja)" enabled={f.mpesa_enabled} onToggle={toggle("mpesa_enabled")} testid="mpesa-enabled" />
            <div className="grid md:grid-cols-3 gap-3">
              <label><span className="vtx-eyebrow block mb-1.5">Label</span><input className="vtx-input" value={f.mpesa_label} onChange={set("mpesa_label")} data-testid="mpesa-label" /></label>
              <label><span className="vtx-eyebrow block mb-1.5">Business shortcode</span><input className="vtx-input font-mono" value={f.mpesa_shortcode} onChange={set("mpesa_shortcode")} placeholder="174379" data-testid="mpesa-shortcode" /></label>
              <label><span className="vtx-eyebrow block mb-1.5">Environment</span>
                <select className="vtx-input" value={f.mpesa_env} onChange={set("mpesa_env")} data-testid="mpesa-env">
                  <option value="sandbox">Sandbox</option>
                  <option value="production">Production</option>
                </select>
              </label>
              <label><span className="vtx-eyebrow block mb-1.5">Consumer key (encrypted)</span><input className="vtx-input font-mono" value={f.mpesa_consumer_key} onChange={set("mpesa_consumer_key")} placeholder="leave blank to keep" data-testid="mpesa-ck" /></label>
              <label><span className="vtx-eyebrow block mb-1.5">Consumer secret (encrypted)</span><input className="vtx-input font-mono" value={f.mpesa_consumer_secret} onChange={set("mpesa_consumer_secret")} placeholder="leave blank to keep" data-testid="mpesa-cs" /></label>
              <label><span className="vtx-eyebrow block mb-1.5">Passkey (encrypted)</span><input className="vtx-input font-mono" value={f.mpesa_passkey} onChange={set("mpesa_passkey")} placeholder="leave blank to keep" data-testid="mpesa-passkey" /></label>
              <label className="md:col-span-2"><span className="vtx-eyebrow block mb-1.5">STK callback URL</span><input className="vtx-input font-mono text-xs" value={f.mpesa_callback_url} onChange={set("mpesa_callback_url")} placeholder="https://yourapi.com/api/v1/payments/mpesa/callback?token=…" data-testid="mpesa-callback" /></label>
              <label><span className="vtx-eyebrow block mb-1.5">Callback token (encrypted)</span><input className="vtx-input font-mono" value={f.mpesa_callback_token} onChange={set("mpesa_callback_token")} placeholder="leave blank to keep" data-testid="mpesa-callback-token" /></label>
            </div>
            <p className="text-[11px] text-[#475569]">Callbacks are unsigned; settlement only matches STK pushes we initiated. For an extra guard, set a token here and append <span className="font-mono">?token=…</span> to the callback URL above.</p>
          </div>

          <div className="lg:col-span-2 flex justify-end">
            <button className="vtx-btn-primary inline-flex items-center gap-2" disabled={busy} onClick={save} data-testid="save-payments"><Save size={14} /> Save payment settings</button>
          </div>
        </div>
      )}
    </PortalLayout>
  );
}
